import type { ZodTypeAny, output } from 'zod';
import { toApiError } from './problem';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
const TOKEN_KEY = 'swi.airship.token';

/**
 * The session token.
 *
 * Kept in memory and mirrored into localStorage, not the other way round: a
 * private window or blocked site data then still gives a working session for as
 * long as the tab is open, it just is not remembered afterwards.
 */
let memoryToken: string | null = readPersisted();

function readPersisted(): string | null {
  try {
    return globalThis.localStorage?.getItem(TOKEN_KEY) ?? null;
  } catch {
    return null;
  }
}

export function storedToken(): string | null {
  return memoryToken;
}

export function storeToken(token: string | null): void {
  memoryToken = token;
  try {
    if (token === null) {
      globalThis.localStorage?.removeItem(TOKEN_KEY);
    } else {
      globalThis.localStorage?.setItem(TOKEN_KEY, token);
    }
  } catch {
    // Site data blocked — the session lives for this tab only.
  }
}

type Unauthorized = () => void;
let onUnauthorized: Unauthorized = () => undefined;
export const setUnauthorizedHandler = (handler: Unauthorized): void => {
  onUnauthorized = handler;
};

interface RequestOptions<S extends ZodTypeAny> {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Contract schema the response is parsed with — never skipped. */
  schema: S;
  signal?: AbortSignal;
}

/**
 * The single place the frontend talks to the API.
 *
 * Every response is parsed with its schema from @swi/contracts, so a backend
 * that drifts from the contract fails here with a clear message instead of
 * producing `undefined` somewhere deep in a component.
 */
export async function request<S extends ZodTypeAny>(
  path: string,
  options: RequestOptions<S>,
): Promise<output<S>> {
  const token = storedToken();

  const response = await fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    signal: options.signal ?? null,
  });

  const text = await response.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { raw: text };
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      onUnauthorized();
    }
    throw toApiError(response.status, payload);
  }

  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .slice(0, 5)
      .map((issue) => `${issue.path.join('.') || '_'}: ${issue.message}`)
      .join('; ');
    throw new Error(`Odpověď ${path} nesouhlasí s kontraktem (@swi/contracts): ${issues}`);
  }
  return parsed.data as output<S>;
}

/** Builds a query string, skipping empty values. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const serialised = search.toString();
  return serialised.length > 0 ? `?${serialised}` : '';
}
